import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";

const CaptchaContainer = styled.div`
  --panel: rgba(31, 24, 45, 0.7);
  --border: #3b2a59;
  --accent: #9535fc;
  --accent-2: #7c3aed;
  --good: #10b981;
  --bad: #ef4444;

  background: linear-gradient(180deg, var(--panel), rgba(23,21,29,0.6));
  border: 1px solid var(--border);
  border-radius: 16px;
  padding: 18px;
  backdrop-filter: blur(8px);
  box-shadow: 0 6px 28px rgba(149, 53, 252, 0.12) inset, 0 2px 0 rgba(255,255,255,0.04);
`;

const Title = styled.div`
  font: 600 14px/1 Inter, ui-sans-serif;
  color: #fff;
  text-align: center;
  margin-bottom: 6px;
  display: flex; align-items: center; justify-content: center; gap: 6px;
`;

const Description = styled.div`
  font: 500 12px/1.4 Inter, ui-sans-serif;
  color: #b9b3c7;
  text-align: center;
  margin-bottom: 12px;
`;

const Card = styled.div`
  position: relative;
  width: 100%;
  max-width: 420px;
  margin: 0 auto 12px;
  border-radius: 12px;
  overflow: hidden;
  background: radial-gradient(120% 120% at 0% 0%, #2b1840 0%, #1a1129 60%, #120b1c 100%);
  box-shadow: inset 0 0 0 1px rgba(124,58,237,0.15);
  padding: 14px;
`;

const Prompt = styled.div`
  font: 600 13px/1 Inter, ui-sans-serif;
  color: #d7d2e6;
  text-align: center;
  margin-bottom: 10px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
`;

const Cell = styled.button<{ disabled?: boolean }>`
  height: 64px;
  border-radius: 12px;
  border: 1px solid rgba(149, 53, 252, 0.28);
  background:
    linear-gradient(180deg, rgba(255,255,255,0.06), rgba(0,0,0,0.16)),
    linear-gradient(90deg, #26143f, #1c1231);
  color: #fff;
  font: 700 26px/1 Inter, ui-sans-serif;
  display: grid; place-items: center;
  cursor: ${p => (p.disabled ? "not-allowed" : "pointer")};
  transition: transform 120ms ease, box-shadow 120ms ease, background 200ms ease;
  outline: none;
  user-select: none;
  touch-action: manipulation;

  &:hover { transform: translateY(-1px); }
  &:active { transform: translateY(0px) scale(0.98); }
  &:focus-visible { box-shadow: 0 0 0 2px #fff, 0 0 0 4px rgba(149,53,252,0.6); }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const FooterRow = styled.div`
  display: flex; gap: 8px; justify-content: center; align-items: center; margin-top: 8px;
`;

const SmallBtn = styled.button`
  height: 34px; padding: 0 12px; border-radius: 8px; border: 0;
  background: linear-gradient(90deg, #9535fc, #7c3aed);
  color: #fff; font: 600 12px Inter, ui-sans-serif;
  cursor: pointer;
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const Status = styled.div<{ ok?: boolean; err?: boolean }>`
  font: 600 12px Inter, ui-sans-serif; min-height: 16px;
  color: ${p => p.ok ? "#10b981" : p.err ? "#ef4444" : "#b9b3c7"};
  text-align: center; margin-top: 6px;
`;

const SrLive = styled.div.attrs({ role: "status", "aria-live": "polite" })`
  position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0);
`;

type Props = { onSuccess: () => void; onFail?: () => void };

/**
 * Drop-in Emoji Match CAPTCHA.
 * - 100% client-side
 * - Same export & props as your slider
 * - Small anti-bot: min solve time, brief cooldown on failure, shuffling
 */
export const SliderCaptcha: React.FC<Props> = ({ onSuccess, onFail }) => {
  // Config
  const pool = useMemo(
    () => ["🍎","🍌","🍇","🍒","🍉","🍍","🥝","🍑","🍋","🍓","🥥","🥭","🫐","🍐","🍊","🍈"],
    []
  );
  const gridSize = 9; // 3x3
  const minSolveMs = 800; // require at least this much time before accepting a correct click
  const cooldownMs = 900; // lock buttons briefly after a wrong answer

  // State
  const [target, setTarget] = useState<string>("");
  const [grid, setGrid] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ text: string; ok?: boolean; err?: boolean } | null>(null);

  const startRef = useRef<number>(Date.now());
  const lockedRef = useRef(false);

  function shuffle<T>(arr: T[]): T[] {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  }

  function regen() {
    setBusy(true);
    setMsg(null);
    lockedRef.current = false;
    startRef.current = Date.now();

    // pick target
    const t = pool[Math.floor(Math.random() * pool.length)];

    // pick grid: ensure target included at least once
    const choices = shuffle(pool).slice(0, gridSize - 1);
    choices.splice(Math.floor(Math.random() * gridSize), 0, t); // insert target
    setTarget(t);
    setGrid(choices);
    setBusy(false);
  }

  useEffect(() => { regen(); /* on mount */ }, []);

  function handlePick(v: string) {
    if (busy || lockedRef.current) return;
    // brief local rate limit on spam clicking
    lockedRef.current = true;
    setTimeout(() => { lockedRef.current = false; }, 120);

    const elapsed = Date.now() - startRef.current;
    const correct = v === target;

    if (correct && elapsed >= minSolveMs) {
      setMsg({ text: "✓ Verified", ok: true });
      try { navigator.vibrate?.(30); } catch {}
      onSuccess();
      return;
    }

    // either wrong, or solved too fast → treat as fail
    setMsg({ text: "✗ Incorrect. Try again.", err: true });
    try { navigator.vibrate?.([10, 40, 10]); } catch {}
    onFail?.();
    setBusy(true);
    setTimeout(() => { setBusy(false); regen(); }, cooldownMs);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    // simple keyboard support: number keys 1..9 select cells left→right, top→bottom
    if (!grid.length) return;
    const idx = parseInt(e.key, 10);
    if (!Number.isNaN(idx) && idx >= 1 && idx <= grid.length) {
      e.preventDefault();
      handlePick(grid[idx - 1]);
    }
    if (e.key === "r" || e.key === "R") {
      e.preventDefault();
      regen();
    }
  }

  return (
    <CaptchaContainer onKeyDown={onKeyDown}>
      <Title>🔒 HUMAN VERIFICATION</Title>
      <Description>Select the requested emoji to continue</Description>

      <Card>
        <Prompt>
          Select <span style={{ fontWeight: 800, color: "#fff" }}>{target || "…"}</span>
        </Prompt>

        <Grid role="listbox" aria-label="Emoji choices">
          {grid.map((e, i) => (
            <Cell
              key={`${e}-${i}`}
              onClick={() => handlePick(e)}
              aria-label={`Choice ${i + 1}: ${e}`}
              disabled={busy}
            >
              {e}
            </Cell>
          ))}
        </Grid>

        <FooterRow>
          <SmallBtn onClick={regen} disabled={busy} aria-label="Refresh challenge">↻ Refresh</SmallBtn>
        </FooterRow>

        <Status ok={msg?.ok} err={msg?.err}>{msg?.text ?? ""}</Status>
        <SrLive>{msg?.text ?? ""}</SrLive>
      </Card>
    </CaptchaContainer>
  );
};

export default SliderCaptcha;
